
import { Component, Vue } from 'vue-property-decorator';
import { Action, Getter } from 'vuex-class';
import { DateTime } from 'luxon';
import { alphabeticalSort } from '@/utils/sort';
import { formatCustomQuestionAnswer, getOrderPaymentPreference, getDisplayOrderNumber, getOrderType } from '@/utils/getters';
import { formatPhoneNumberForDisplay } from '@/utils/format';
import Toast from '@/utils/toast';
import OrderItemsList from '@/components/suites/beo/OrderItemsList.vue';
import OrderItem from '@/components/suites/beo/OrderItem.vue';
import OrdersTable from '@/components/report/OrdersTable.vue';
import ChitPrint from '@/components/report/ChitPrint.vue';
import OrdersReportActions from '@/components/report/OrdersReportActions.vue';
import OrdersReportExportModal from '@/components/report/OrdersReportExportModal.vue';
import SuitesHeader from '@/components/shared/suites/SuitesHeader.vue';
import SuitesPanel from '@/components/shared/suites/panels/SuitesPanel.vue';
import TextButton from '@/components/shared/suites/TextButton.vue';
import DatePicker from '@/components/shared/DatePicker.vue';
import SelectComponent from '@/components/shared/Select.vue';

const namespace = 'auth';

const SCHOOL_SORT_OPTIONS = ['grade', 'classroom', 'student', 'teacher'] as const;
type SchoolReportSortOption = typeof SCHOOL_SORT_OPTIONS[number];

/**
 * Type guard narrowing a raw sort value to a school-specific sort option.
 *
 * @param {string} value - The currently selected sort value.
 * @return {boolean} True when the value is one of the school sort options.
 */
function isSchoolSortOption(value: string): value is SchoolReportSortOption {
	return (SCHOOL_SORT_OPTIONS as readonly string[]).includes(value);
}

// If modifying this component, review whether similar updates are required in the ChitPrint component.
@Component<OrdersReport>({
	components: {
		SelectComponent,
		TextButton,
		OrderItemsList,
		OrderItem,
		SuitesHeader,
		SuitesPanel,
		DatePicker,
		OrdersTable,
		ChitPrint,
		OrdersReportExportModal,
		OrdersReportActions
	}
})
export default class OrdersReport extends Vue {
	@Action('exportOrdersReport', { namespace }) private exportOrdersReport!: (queryParams: ExportReportQueryParams) => Promise<{ url: string }>;
	@Action('fetchOrdersReport', { namespace }) private fetchOrdersReport!: (queryParams: GetReportQueryParams) => Promise<{ groupedItems: OrderItemGroup[], orders: EventSuiteOrder[]}>;
	@Getter('getRestaurantDeliveryInfo', { namespace }) private deliveryInfo!: Delivery | null;
	@Getter('getExtraPaymentMethods', { namespace }) private extraPaymentMethods!: RestaurantExtraPaymentMethod[] | null;
	@Getter('isK12Location', { namespace }) private isK12Location!: boolean;
	@Getter('getTimezone', { namespace }) private timezone!: string;
	@Getter('getRestaurants', { namespace }) private restaurants!: Tenant[];

	private selectedDateRange: string[] = [];
	private selectedCreatedAtDateRange: string[] = [];
	private reportDateRange: string[] = [];
	private schoolOptions: { text: string; value: number; }[] = [];
	private selectedSchool: number = 0;
	private showExportModal: boolean = false;
	private isExporting: boolean = false;
	private isFetching: boolean = false;
	private includePrices: boolean = false;
	private ordersSummary: OrderItemGroup[] | null = null;
	private orders: EventSuiteOrder[] | null = null;
	private sortSelected: 'date' | SchoolReportSortOption = 'date';
	private printOption: 'compact'|'order_per_page'|'chit' = 'compact';
	private sortOptions: { label: string, value: string }[] = ['date', ...SCHOOL_SORT_OPTIONS].map((value) => ({
		label: this.$t(`orders.report.orders_summary.sort_options.${value}`),
		value
	}));

	private get formattedDates(): { startDate: string, endDate: string } | null {
		return this.reportDateRange.length ? {
			startDate: DateTime.fromISO(this.reportDateRange[0]).toFormat(this.$t('orders.report.report_input_date_format')),
			endDate: DateTime.fromISO(this.reportDateRange[1]).toFormat(this.$t('orders.report.report_input_date_format'))
		} : null;
	}

	private get sortedOrders(): EventSuiteOrder[] | null {
		if (this.orders === null) {
			return null;
		}
		const sortKey = this.sortSelected;
		if (this.deliveryInfo?.type === 'school' && isSchoolSortOption(sortKey)) {
			return [...this.orders].sort((a, b) => alphabeticalSort(
				a.data.checkout.delivery?.[sortKey] ?? '',
				b.data.checkout.delivery?.[sortKey] ?? ''
			));
		}
		return this.orders;
	}

	private created(): void {
		if (this.deliveryInfo?.type === 'school' && this.deliveryInfo.schools && this.deliveryInfo.schools.length > 1) {
			this.schoolOptions = [
				{ text: this.$t('orders.report.all_schools_text'), value: 0 },
				...this.deliveryInfo.schools.map((school, index) => {
					return {
						text: school.name,
						value: index + 1
					};
				})];
		}
	}

	/**
	 * Fetch orders report for the selected date range
	 *
	 * @return {Promise<void>}
	 */
	private async getReport(): Promise<void> {
		this.isFetching = true;
		const school: string = this.selectedSchool > 0 && this.schoolOptions.length ? this.schoolOptions[this.selectedSchool].text : '';
		try {
			const res = await this.fetchOrdersReport({
				dateRange: this.selectedDateRange.length === 2 ? this.selectedDateRange : [],
				createdAtDateRange: this.selectedCreatedAtDateRange.length === 2 ? this.selectedCreatedAtDateRange : [],
				school
			});
			this.orders = res.orders;
			this.ordersSummary = res.groupedItems;
			this.reportDateRange = this.selectedDateRange.length === 2 ? this.selectedDateRange : this.selectedCreatedAtDateRange;
		}
		catch (errorMessage) {
			this.orders = [];
			this.ordersSummary = [];
			this.reportDateRange = [];
			Toast.error(this.$t('orders.report.error_fetching', { errorMessage }));
		}
		finally {
			this.isFetching = false;
		}
	}

	/**
	 * Check if an order has delivery or pickup instructions
	 *
	 * @param {EventSuiteOrder} order
	 * @return {boolean}
	 */
	private doesOrderHaveInstructions(order: EventSuiteOrder): boolean {
		return !!(order.data.checkout?.pickup?.notes || order.data.checkout?.delivery);
	}

	/**
	 * Get the order number to display, prefixed with the restaurant's internal identifier.
	 *
	 * @param {EventSuiteOrder} order
	 * @return {string}
	 */
	private getOrderNumber(order: EventSuiteOrder): string {
		return getDisplayOrderNumber(order.id);
	}

	private getOrderType(order: EventSuiteOrder): string {
		return getOrderType(order).type;
	}

	/**
	 * Formats the answer of a custom question.
	 *
	 * @param {AnsweredCustomQuestion} answeredQuestion
	 * @return {string}
	 */
	private formatCustomQuestionAnswer(answeredQuestion: AnsweredCustomQuestion): string {
		return formatCustomQuestionAnswer(answeredQuestion);
	}

	/**
	 * Get delivery time for suite orders
	 *
	 * @param {EventSuiteOrder} order
	 * @return {string | null}
	 */
	private getSuitesDeliveryTime(order: EventSuiteOrder): string | null {
		if (order.data.suites && order.data.suites.orderDeliveryTime) {
			const deliveryTime: DateTime = DateTime.fromISO(order.data.suites.orderDeliveryTime).setZone(this.timezone);
			if (deliveryTime.isValid) {
				return deliveryTime.toFormat(this.$t('suites.beo.summary.delivery_times.datetime.date_format'));
			}
			return this.$t(`suites.beo.summary.delivery_times.basic.${order.data.suites.orderDeliveryTime}`);
		}
		return null;
	}

	/**
	 * Get the payment preference for the order
	 *
	 * @param {Order} order
	 * @return {string | undefined}
	 */
	private orderPaymentPreference(order: Order): string | undefined {
		return getOrderPaymentPreference(order ,this.extraPaymentMethods);
	}

	/**
	 * Format the guest phone number for display
	 *
	 * @param {string} [phoneNumber]
	 * @return {string}
	 */
	private formatGuestPhone(phoneNumber?: string): string {
		return formatPhoneNumberForDisplay(phoneNumber);
	}

	/**
	 * Print the orders report page
	 *
	 * @param {'report'|'chit'} option
	 * @return {void}
	 */
	private printReport(option: string): void {
		this.printOption = option as 'compact'|'order_per_page'|'chit';
		document.title = this.$t('orders.report.print_title', this.formattedDates!);
		history.replaceState(null, '', `/${this.$t('orders.report.print_url')}`);
		this.$nextTick(() => {
			window.print();
			document.title = this.$t('orders.report.title');
			history.replaceState(null, '', '/orders-report');
		});
	}

	/**
	 * Export the orders report to an Excel file.
	 *
	 * @param {string[]} [selectedRestaurantIds = []]
	 * @return {Promise<void>}
	 */
	private async exportData(selectedRestaurantIds: string[] = []): Promise<void> {
		this.isExporting = true;
		const locations: string[] = this.restaurants.length === 1 ? [this.restaurants[0].id] : selectedRestaurantIds;
		try {
			await this.exportOrdersReport({
				dateRange: this.selectedDateRange.length === 2 ? this.selectedDateRange : [],
				createdAtDateRange: this.selectedCreatedAtDateRange.length === 2 ? this.selectedCreatedAtDateRange : [],
				includePrices: this.includePrices,
				locations,
				sort: this.sortSelected !== 'date' ? this.sortSelected : undefined
			});

			Toast.success(this.$t('orders.report.export_success_message'));
			this.isExporting = false;
			this.showExportModal = false;
		}
		catch (errorMessage) {
			this.isExporting = false;
			Toast.error(this.$t('orders.report.error_exporting', { errorMessage }));
		}
	}
}
